import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Link,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  SvgIcon,
  Alert,
  Checkbox,
  FormGroup,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputText } from "../../../elements/InputText";
import SelectControl from "../../../elements/SelectControl";
import { ErrorBox } from "../../../elements/ErrorBox";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CustomTimePicker } from "../../../elements/CustomTimePicker";
import { CustomDatePicker } from "../../../elements/CustomDatePicker";
import GooglePlacesAutocomplete from "../../../elements/GooglePlacesAutocomplete";
import { TermsOfServiceDialog } from "../../Modals/TermsOfServiceDialog";
import { PaymentElement } from "@stripe/react-stripe-js";
import { BasicCheckbox } from "../../../elements/BasicCheckbox";
import RadioControl from "../../../elements/RadioControl";
import { RebookQuestionnaireSection } from "../RebookQuestionnaireSection";
import {
  isSameDayOrWeekend,
  OFFICE_PHONE,
  SAME_DAY_WEEKEND_MESSAGE,
} from "../../../utils/sameDayWeekend";
import { STORAGE_USER } from "../../../constants/storage";
import {
  bookingWindowIssue,
  bookingWindowMessage,
  combineDenverDateAndTime,
  earliestStartMinutes,
  minutesFromMidnight,
  missingBookingFields,
  missingBookingFieldsMessage,
} from "../bookingTime";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const FREQUENCY_OPTIONS = [
  { label: "Every week", value: "1" },
  { label: "Every 2 weeks", value: "2" },
  { label: "Every 4 weeks (monthly)", value: "4" },
];

const savedServiceAddress = () => {
  try {
    const user = JSON.parse(localStorage.getItem(STORAGE_USER) || "{}");
    return {
      address: user?.client?.serviceAddress || "",
      latitude: user?.client?.serviceLatitude,
      longitude: user?.client?.serviceLongitude,
    };
  } catch {
    return { address: "", latitude: undefined, longitude: undefined };
  }
};

const isRecurringValue = (value: unknown) => value === true || value === "true";

interface IJobState {
  [key: string]: any;
}

type TPostJobStep3Props = {
  jobState: any;
  jobStep: number;
  rebookJob?: any;
  priceEstimate: string;
  paymentMethod: any;
  responseError: string | null;
  handleUpdateJobState: (data: IJobState) => void;
  changeStep: (step: number) => void;
  startDateTime: Date | null;
  setStartDateTime: (date: Date | null) => void;
  endDateTime: Date | null;
  setEndDateTime: (date: Date | null) => void;
  loading?: boolean;
  rebookLoading?: boolean;
  onQuestionnaireChange?: (content: any[] | null, hasPrevious: boolean) => void;
  onSaveDraft?: (data: IJobState) => void;
};

export const PostJobStep3 = ({
  jobState,
  priceEstimate,
  rebookJob,
  paymentMethod,
  handleUpdateJobState,
  changeStep,
  jobStep,
  responseError,
  startDateTime,
  setStartDateTime,
  endDateTime,
  setEndDateTime,
  loading = false,
  rebookLoading = false,
  onQuestionnaireChange,
  onSaveDraft,
}: TPostJobStep3Props) => {
  const { palette } = useTheme();
  const [open, setOpen] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [dateEntryError, setDateEntryError] = useState<string | null>(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      saveMethod: false,
      termsService: false,
      repeats: isRecurringValue(jobState?.repeats),
      often: jobState?.often ? String(jobState.often) : "1",
      weekdays: jobState?.weekdays || [],
      endMode: jobState?.endMode || "date",
      visitCount: jobState?.visitCount || 4,
      expirationDate: jobState?.expirationDate,
      address: jobState?.address || savedServiceAddress().address,
      latitude: jobState?.latitude ?? savedServiceAddress().latitude,
      longitude: jobState?.longitude ?? savedServiceAddress().longitude,
      name: jobState?.name || "",
      description: jobState?.description || "",
      date: jobState?.date,
      startTime: jobState?.startTime,
      endTime: jobState?.endTime,
    },
    resolver: yupResolver(schema),
  });

  const defaultDate = dayjs().toDate();
  const startDate = watch("date");
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const repeats = watch("repeats");
  const serviceName = watch("name");
  const serviceNotes = watch("description");
  const endMode = watch("endMode");
  const weekdays = watch("weekdays");
  const visitCount = watch("visitCount");
  const often = watch("often");
  const expirationDate = watch("expirationDate");
  const address = watch("address");
  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const windowIssue = bookingWindowIssue(startDate, startTime, endTime);
  const scheduleMessage = dateEntryError || (windowIssue ? bookingWindowMessage(windowIssue) : "");
  const missingFields = missingBookingFields({
    name: serviceName,
    description: serviceNotes,
    address,
    latitude,
    longitude,
    recurring: !rebookJob && isRecurringValue(repeats),
    weekdays,
    endMode,
    visitCount,
    expirationDate,
  });
  const detailsMessage = missingBookingFieldsMessage(missingFields);
  const minStartMinutes = earliestStartMinutes(startDate || new Date());
  const minEndMinutes = startTime ? minutesFromMidnight(startTime) + 30 : null;

  useEffect(() => {
    if (!startDate || !startTime || !endTime || windowIssue || dateEntryError) {
      setStartDateTime(null);
      setEndDateTime(null);
      return;
    }

    setStartDateTime(combineDenverDateAndTime(startDate, startTime).toDate());
    setEndDateTime(combineDenverDateAndTime(startDate, endTime).toDate());
  }, [setStartDateTime, setEndDateTime, startDate, startTime, endTime, windowIssue, dateEntryError]);


  useEffect(() => {
    if (rebookJob) {
      setValue("name", rebookJob?.name);
      setValue("description", rebookJob?.description);
      if (rebookJob.site) {
        setValue("address", rebookJob.site.address);
        setValue("latitude", rebookJob.site.latitude);
        setValue("longitude", rebookJob.site.longitude);
      }
      if (rebookJob.startDate) {
        setValue("startTime", dayjs(rebookJob.startDate).toDate());
      }
      if (rebookJob.endDate) {
        setValue("endTime", dayjs(rebookJob.endDate).toDate());
      }
    }
  }, [rebookJob, setValue]);

  const onSubmit = async (data: any): Promise<any> => {
    if (!rebookJob && isRecurringValue(data.repeats)) {
      const days = data.weekdays || [];
      if (!days.length) {
        setError("weekdays", { type: "custom", message: "Select at least one day" });
        return;
      }
      const startWeekday = data.date ? dayjs(data.date).format("dddd") : "";
      if (startWeekday && !days.includes(startWeekday)) {
        setError("weekdays", {
          type: "custom",
          message: `The start date is a ${startWeekday}. Include that day, or change the date.`,
        });
        return;
      }
    }

    if (!paymentMethod) {
      if (getValues("saveMethod") === false) {
        setError("saveMethod", { type: "custom", message: "This is required" });
      }

      if (getValues("termsService") === false) {
        setError("termsService", {
          type: "custom",
          message: "This is required",
        });
      }

      if (
        getValues("saveMethod") === false ||
        getValues("termsService") === false
      ) {
        return;
      }
    }

    handleUpdateJobState(data);
  };

  const handleChangeAutocomplete = (data: any) => {
    setValue("address", data?.description);
    setValue("latitude", data?.cordinates?.lat);
    setValue("longitude", data?.cordinates?.lng);
  };

  return (
    <>
      <Box sx={{ width: "100%", mb: "24px" }}>
        <Typography
          align="center"
          variant="h2"
          sx={{
            mb: "24px",
          }}
        >
          {rebookJob ? "When do you need this again?" : "Client information"}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
          >
            {rebookJob && (
              <>
                <input type="hidden" {...register("address")} />
                <input type="hidden" {...register("latitude", { valueAsNumber: true })} />
                <input type="hidden" {...register("longitude", { valueAsNumber: true })} />
                <Box
                  sx={{
                    position: "relative",
                    mb: "8px",
                    width: "100%",
                  }}
                >
                  <Controller
                    control={control}
                    name="date"
                    defaultValue={defaultDate}
                    render={({ field: { onChange, value, ref } }) => (
                      <CustomDatePicker
                        fullWidth
                        onChange={(next: unknown) => {
                          setDateEntryError(null);
                          onChange(next);
                        }}
                        onError={(reason: string | null) => {
                          if (!reason) {
                            setDateEntryError(null);
                            return;
                          }
                          setDateEntryError(
                            reason === "invalidDate"
                              ? "Enter a valid date."
                              : "Choose today or a future date."
                          );
                        }}
                        value={value ? dayjs(value) : null}
                        inputRef={ref}
                        label={"New date"}
                        views={["month", "day"]}
                        minDate={dayjs().startOf("day")}
                        slotProps={{
                          textField: {
                            error: Boolean(dateEntryError || errors.date),
                            helperText: dateEntryError || (errors.date ? errors.date.message : ""),
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ display: "flex", mb: "20px" }}>
                  <Controller
                    name="startTime"
                    control={control}
                    defaultValue={dayjs().toDate()}
                    render={({ field: { onChange, value, ref } }) => (
                      <CustomTimePicker
                        sx={{ mr: "16px" }}
                        label="Start Time"
                        onChange={onChange}
                        onAccept={onChange}
                        value={value ? dayjs(value) : null}
                        minMinutesFromMidnight={minStartMinutes > 0 ? minStartMinutes : undefined}
                        inputRef={ref}
                        orientation={"portrait"}
                        slotProps={{
                          textField: {
                            helperText: errors.startTime
                              ? errors.startTime.message
                              : "",
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="endTime"
                    control={control}
                    defaultValue={dayjs().add(30, "minute").toDate()}
                    render={({ field: { onChange, value, ref } }) => (
                      <CustomTimePicker
                        label="End Time"
                        onChange={onChange}
                        onAccept={onChange}
                        value={value ? dayjs(value) : null}
                        minMinutesFromMidnight={
                          minEndMinutes == null ? undefined : Math.min(minEndMinutes, 24 * 60)
                        }
                        inputRef={ref}
                        orientation={"portrait"}
                        slotProps={{
                          textField: {
                            helperText: errors.endTime
                              ? errors.endTime.message
                              : "",
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                <Box
                  sx={{
                    mb: "20px",
                    p: "16px",
                    border: "1px solid #E3E7E3",
                    borderRadius: "12px",
                    backgroundColor: "#F7F9F7",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "12px",
                      mb: "8px",
                    }}
                  >
                    <Typography sx={{ fontSize: "16px", lineHeight: "22px", fontWeight: 600 }}>
                      Same job as last time
                    </Typography>
                    <Button
                      onClick={() => setIsEditingDetails((prev) => !prev)}
                      sx={{
                        px: 0,
                        minWidth: 0,
                        flexShrink: 0,
                        fontSize: "13px",
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#316E59",
                      }}
                    >
                      {isEditingDetails ? "Done" : "Edit details"}
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: "#455445", mb: "12px" }}>
                    Last time’s details and access notes will be copied. Edit only if something changed.
                  </Typography>
                  <Box sx={{ mb: "12px" }}>
                    <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B" }}>
                      Service
                    </Typography>
                    <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                      {rebookJob?.category?.name || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: "12px" }}>
                    <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B" }}>
                      Address
                    </Typography>
                    <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                      {rebookJob?.site?.address || "—"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: isEditingDetails ? "block" : "none" }}
                    aria-hidden={!isEditingDetails}
                  >
                    <Box sx={{ mb: "4px" }}>
                      <InputText
                        inputProps={{ ...register("name"), maxLength: 120 }}
                        type="text"
                        label="Service name"
                        placeholder="Service name"
                        error={!!errors["name"]}
                        helperText={errors["name"] ? errors["name"].message : ""}
                        fullWidth
                      />
                    </Box>
                    <InputText
                      inputProps={{ ...register("description"), maxLength: 2000 }}
                      type="text"
                      multiline
                      rows={2}
                      label="Notes"
                      placeholder="Notes"
                      error={!!errors["description"]}
                      helperText={
                        errors["description"] ? errors["description"].message : ""
                      }
                      fullWidth
                    />
                  </Box>
                  {!isEditingDetails && (
                    <>
                      <Box sx={{ mb: "12px" }}>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B" }}>
                          Service name
                        </Typography>
                        <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                          {serviceName || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B" }}>
                          Notes
                        </Typography>
                        <Typography sx={{ fontSize: "14px", lineHeight: "20px", whiteSpace: "pre-wrap" }}>
                          {serviceNotes || "—"}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {onQuestionnaireChange && (
                    <RebookQuestionnaireSection
                      jobId={rebookJob.id}
                      isEditing={isEditingDetails}
                      onAnswersChange={onQuestionnaireChange}
                    />
                  )}
                </Box>

                <Box sx={{ mb: repeats ? "12px" : "4px" }}>
                  <Controller
                    name="repeats"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{
                          ml: 0,
                          mr: 0,
                          alignItems: "center",
                          "& .MuiCheckbox-root": {
                            p: 0,
                            mr: "10px",
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            lineHeight: "20px",
                          },
                        }}
                        control={
                          <BasicCheckbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Make this a recurring job"
                      />
                    )}
                  />
                </Box>
                {repeats && (
                  <>
                    <Box sx={{ mb: "16px" }}>
                      <FormLabel>How often do you need this service?</FormLabel>
                      <Controller
                        control={control}
                        name="often"
                        render={({ field }) => (
                          <RadioGroup sx={{ mt: "7px" }} {...field}>
                            {FREQUENCY_OPTIONS.map((item) => (
                              <FormControlLabel
                                key={item.value}
                                control={<RadioControl value={item.value} />}
                                label={item.label}
                              />
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    <Box sx={{ mb: "8px" }}>
                      <FormLabel>When will you no longer need this service?</FormLabel>
                      <Controller
                        control={control}
                        name="expirationDate"
                        defaultValue={dayjs().toDate()}
                        render={({ field: { onChange, value, ref } }) => (
                          <CustomDatePicker
                            fullWidth
                            onChange={onChange}
                            value={dayjs(value)}
                            inputRef={ref}
                            label={"End date"}
                            views={["month", "day"]}
                            minDate={dayjs()}
                            slotProps={{
                              textField: {
                                helperText: errors["expirationDate"]
                                  ? errors["expirationDate"].message
                                  : "",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </>
                )}
              </>
            )}

            {!rebookJob && (
              <>
            <Box sx={{ mb: "4px" }}>
              <InputText
                inputProps={{ ...register("name"), maxLength: 120 }}
                type="text"
                label="Service name"
                placeholder="Service name"
                error={!!errors["name"]}
                helperText={errors["name"] ? errors["name"].message : ""}
                fullWidth
              />
            </Box>
            <Box sx={{ mb: "4px" }}>
              <InputText
                inputProps={{ ...register("description"), maxLength: 2000 }}
                type="text"
                multiline
                rows={2}
                label="Description"
                placeholder="Description"
                error={!!errors["description"]}
                helperText={
                  errors["description"] ? errors["description"].message : ""
                }
                fullWidth
              />
            </Box>
            <Box sx={{ mb: "4px" }}>
              <GooglePlacesAutocomplete
                handleChange={handleChangeAutocomplete}
                label="Service address"
                placeholder="Start typing your address"
                noOptionsText="Enter your service address"
                initialAddress={jobState?.address || savedServiceAddress().address}
                rebookSite={rebookJob?.site}
              />
              <Typography variant="body2" sx={{ mt: "4px", mb: "8px", color: "#6B746B" }}>
                Start typing, then choose your address from the list.
              </Typography>
              {errors["address"] && (
                <ErrorBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textAlign: "left",
                  }}
                >
                  {errors["address"].message}
                </ErrorBox>
              )}
              {(errors["latitude"] || errors["longitude"]) && !errors["address"] && (
                <ErrorBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textAlign: "left",
                  }}
                >
                  {errors["latitude"]?.message || errors["longitude"]?.message}
                </ErrorBox>
              )}
            </Box>
            <Box
              sx={{
                position: "relative",
                mb: "4px",
                width: "100%",
              }}
            >
              <Controller
                control={control}
                name="date"
                defaultValue={defaultDate}
                render={({ field: { onChange, value, ref } }) => (
                  <CustomDatePicker
                    fullWidth
                    onChange={(next: unknown) => {
                      setDateEntryError(null);
                      onChange(next);
                    }}
                    onError={(reason: string | null) => {
                      if (!reason) {
                        setDateEntryError(null);
                        return;
                      }
                      setDateEntryError(
                        reason === "invalidDate"
                          ? "Enter a valid date."
                          : "Choose today or a future date."
                      );
                    }}
                    value={value ? dayjs(value) : null}
                    inputRef={ref}
                    label={"Date"}
                    views={["month", "day"]}
                    minDate={dayjs().startOf("day")}
                    slotProps={{
                      textField: {
                        error: Boolean(dateEntryError || errors.date),
                        helperText: dateEntryError || (errors.date ? errors.date.message : ""),
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex" }}>
              <Controller
                name="startTime"
                control={control}
                defaultValue={dayjs().toDate()}
                render={({ field: { onChange, value, ref } }) => (
                  <CustomTimePicker
                    sx={{ mr: "16px" }}
                    label="Start Time"
                    onChange={onChange}
                    onAccept={onChange}
                    value={value ? dayjs(value) : null}
                    minMinutesFromMidnight={minStartMinutes > 0 ? minStartMinutes : undefined}
                    inputRef={ref}
                    orientation={"portrait"}
                    slotProps={{
                      textField: {
                        helperText: errors.startTime
                          ? errors.startTime.message
                          : "",
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="endTime"
                control={control}
                defaultValue={dayjs().add(30, 'minute').toDate()}
                render={({ field: { onChange, value, ref } }) => (
                  <CustomTimePicker
                    label="End Time"
                    onChange={onChange}
                    onAccept={onChange}
                    value={value ? dayjs(value) : null}
                    minMinutesFromMidnight={
                      minEndMinutes == null ? undefined : Math.min(minEndMinutes, 24 * 60)
                    }
                    inputRef={ref}
                    orientation={"portrait"}
                    slotProps={{
                      textField: {
                        helperText: errors.endTime
                          ? errors.endTime.message
                          : "",
                      },
                    }}
                  />
                )}
              />
            </Box>
            {isRecurringValue(repeats) && (
              <Box sx={{ mt: "12px" }}>
                <FormLabel>How often do you need this service?</FormLabel>
                <Controller
                  control={control}
                  name="often"
                  render={({ field }) => (
                    <RadioGroup sx={{ mt: "7px" }} {...field}>
                      {FREQUENCY_OPTIONS.map((item) => (
                        <FormControlLabel
                          key={item.value}
                          control={<RadioControl value={item.value} />}
                          label={item.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormLabel sx={{ mt: "8px", display: "block" }}>Which days?</FormLabel>
                <Controller
                  control={control}
                  name="weekdays"
                  render={({ field }) => (
                    <FormGroup>
                      {WEEKDAYS.map((day) => (
                        <FormControlLabel
                          key={day}
                          control={
                            <Checkbox
                              size="small"
                              checked={(field.value || []).includes(day)}
                              onChange={(event) => {
                                const current = field.value || [];
                                field.onChange(
                                  event.target.checked
                                    ? [...current, day]
                                    : current.filter((item: string) => item !== day)
                                );
                              }}
                            />
                          }
                          label={day}
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                {errors["weekdays"] && (
                  <ErrorBox>{String(errors["weekdays"]?.message || "Select at least one day")}</ErrorBox>
                )}
                <FormLabel sx={{ mt: "8px", display: "block" }}>How long should it repeat?</FormLabel>
                <Controller
                  control={control}
                  name="endMode"
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      <FormControlLabel control={<RadioControl value="date" />} label="End date" />
                      <FormControlLabel control={<RadioControl value="visits" />} label="Number of visits" />
                    </RadioGroup>
                  )}
                />
                {endMode === "visits" ? (
                  <InputText
                    inputProps={{ ...register("visitCount") }}
                    type="number"
                    label="Number of visits"
                    placeholder="4"
                    error={!!errors["visitCount"]}
                    helperText={errors["visitCount"] ? String(errors["visitCount"].message) : ""}
                    fullWidth
                  />
                ) : (
                  <Controller
                    control={control}
                    name="expirationDate"
                    render={({ field: { onChange, value, ref } }) => (
                      <CustomDatePicker
                        fullWidth
                        onChange={onChange}
                        value={value ? dayjs(value) : null}
                        inputRef={ref}
                        label={"End date"}
                        views={["month", "day"]}
                        minDate={dayjs(startDate || undefined)}
                        slotProps={{
                          textField: {
                            helperText: errors["expirationDate"]
                              ? String(errors["expirationDate"].message)
                              : "",
                          },
                        }}
                      />
                    )}
                  />
                )}
              </Box>
            )}
              </>
            )}
          </Box>
        </LocalizationProvider>
        {priceEstimate && (
          <Box
            sx={{
              mt: "20px",
              px: "12px",
              py: "24px",
              borderRadius: "4px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#8BC33C33",
            }}
          >
            <Typography
              align="center"
              variant="body1"
              sx={{
                fontWeight: "500",
                mb: "12px",
              }}
            >
              Service Price Estimate
            </Typography>
            <Typography
              variant="h1"
              component={"span"}
              sx={{
                mb: "12px",
              }}
            >
              ${Number(priceEstimate).toFixed(0)}
            </Typography>
            {isRecurringValue(repeats) && (
              <Typography align="center" variant="body2" sx={{ mb: "8px" }}>
                {FREQUENCY_OPTIONS.find((item) => item.value === String(often))?.label || "Recurring"}
                {(weekdays || []).length ? ` on ${(weekdays || []).join(", ")}` : ""}
                {endMode === "visits"
                  ? ` · ${visitCount || 0} visits`
                  : expirationDate
                    ? ` through ${dayjs(expirationDate).format("MMM D, YYYY")}`
                    : ""}
                {" · price is per visit"}
              </Typography>
            )}
            <Typography align="center" variant="body2">
              This is an estimate - it does not include applicable taxes and
              fees, such as mileage or reimbursements. See
              <Link
                sx={{
                  textTransform: "none",
                  fontSize: "inherit",
                  lineHeight: "inherit",
                }}
                component="button"
                variant="body2"
                onClick={handleOpen}
              >
                Terms of Service
              </Link>
              .
            </Typography>
          </Box>
        )}

        {!paymentMethod && (
          <Box
            sx={{
              mt: "32px",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SvgIcon sx={{ width: "48px", height: "48px", mb: "16px" }}>
                <svg
                  width="49"
                  height="48"
                  viewBox="0 0 49 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="48"
                    height="48"
                    transform="translate(0.5)"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M33.5 21.375C29.567 21.375 26.375 24.567 26.375 28.5C26.375 32.433 29.567 35.625 33.5 35.625C37.433 35.625 40.625 32.433 40.625 28.5C40.625 24.567 37.433 21.375 33.5 21.375ZM30.5825 30.045L32.0825 31.545C32.3105 31.773 32.6225 31.8915 32.9435 31.8735C33.263 31.854 33.5615 31.701 33.7595 31.449L36.632 27.828C37.0175 27.3405 36.935 26.6325 36.449 26.247C35.963 25.8615 35.2535 25.9425 34.868 26.43L32.7815 29.0625L32.174 28.455C31.7345 28.0155 31.022 28.0155 30.5825 28.455C30.1445 28.893 30.1445 29.607 30.5825 30.045Z"
                    fill="#6FBB4B"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M29.9795 20.625H8.375V30C8.375 31.0935 8.81 32.1435 9.5825 32.9175C10.3565 33.69 11.4065 34.125 12.5 34.125H26.963C25.6625 32.6145 24.875 30.648 24.875 28.5C24.875 24.993 26.972 21.9735 29.9795 20.625ZM18.5015 27.375H14C13.379 27.375 12.875 27.879 12.875 28.5C12.875 29.121 13.379 29.625 14 29.625H18.5015C19.1225 29.625 19.6265 29.121 19.6265 28.5C19.6265 27.879 19.1225 27.375 18.5015 27.375ZM8.375 18.375H37.625V16.5C37.625 15.4065 37.19 14.3565 36.4175 13.5825C35.6435 12.81 34.5935 12.375 33.5 12.375C28.3055 12.375 17.6945 12.375 12.5 12.375C11.4065 12.375 10.3565 12.81 9.5825 13.5825C8.81 14.3565 8.375 15.4065 8.375 16.5V18.375Z"
                    fill="#6FBB4B"
                  />
                </svg>
              </SvgIcon>
              <Typography
                align="center"
                variant="h2"
                sx={{
                  mb: "16px",
                  color: palette.text.primary,
                }}
              >
                Please add a payment method
              </Typography>
              <Typography
                align="center"
                variant="body1"
                sx={{
                  mb: "20px",
                }}
              >
                This is required in order to request a service
              </Typography>
            </Box>
            <Box sx={{ mb: "16px" }}>
              <PaymentElement />
              {responseError && (
                <ErrorBox sx={{ mt: "8px" }}>{responseError}</ErrorBox>
              )}
            </Box>
            <Box
              sx={{
                mb: "16px",
              }}
            >
              <FormControlLabel
                sx={{
                  ml: "3px",
                  color: "#666666",
                  ".MuiFormControlLabel-label": {
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: palette.text.primary,
                    ".MuiLink-button": {
                      borderBottomColor: palette.text.primary,
                      letterSpacing: "0px",
                    },
                  },
                }}
                control={
                  <Controller
                    name="saveMethod"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <BasicCheckbox
                        sx={{ py: "6px" }}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={<>Save this method for future payments</>}
              />
              {errors["saveMethod"] && (
                <ErrorBox
                  sx={{
                    ml: "37px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {errors["saveMethod"].message}
                </ErrorBox>
              )}
              <FormControlLabel
                sx={{
                  ml: "3px",
                  color: "#666666",
                  ".MuiFormControlLabel-label": {
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: palette.text.primary,
                    ".MuiLink-button": {
                      borderBottomColor: palette.text.primary,
                      letterSpacing: "0px",
                    },
                  },
                }}
                control={
                  <Controller
                    name="termsService"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <BasicCheckbox
                        sx={{ py: "5px" }}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={
                  <>
                    Acceptance of{" "}
                    <Link
                      sx={{
                        fontSize: "inherit",
                        lineHeight: "inherit",
                      }}
                      component="button"
                      variant="body2"
                      onClick={handleOpen}
                    >
                      Terms of Service
                    </Link>
                  </>
                }
              />
              {errors["termsService"] && (
                <ErrorBox
                  sx={{
                    ml: "37px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {errors["termsService"].message}
                </ErrorBox>
              )}
            </Box>
          </Box>
        )}
        {isSameDayOrWeekend(startDate) && (
          <Alert severity="warning" sx={{ mt: "20px" }}>
            {SAME_DAY_WEEKEND_MESSAGE} Call or text{" "}
            <Link href={`tel:${OFFICE_PHONE}`} underline="always">
              {OFFICE_PHONE}
            </Link>
            .
          </Alert>
        )}
        {responseError && (
          <ErrorBox
            sx={{
              mt: "30px",
              display: "flex",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {responseError}
          </ErrorBox>
        )}
      </Box>
      <Box
        sx={{
          borderTop: "1px solid #DDDFDD",
          pt: "32px",
          display: "flex",
          width: "100%",
        }}
      >
        {!rebookJob && (
          <Button
            sx={{ mr: "16px" }}
            variant="table"
            disabled={loading}
            onClick={() => {
              onSaveDraft?.(getValues());
              changeStep(jobStep - 1);
            }}
          >
            Back
          </Button>
        )}
        <Button
          fullWidth
          variant="lucky"
          disabled={
            loading ||
            rebookLoading ||
            Boolean(scheduleMessage) ||
            missingFields.length > 0 ||
            !(Number(priceEstimate) > 0) ||
            !paymentMethod
          }
          onClick={handleSubmit(onSubmit)}
          sx={{
            fontSize: "18px",
            lineHeight: "24px",
            py: "12px",
          }}
        >
          {rebookLoading ? "Loading previous job…" : rebookJob ? "Rebook Service" : "Request Service"}
        </Button>
      </Box>
      {(scheduleMessage || missingFields.length > 0 || !(Number(priceEstimate) > 0) || !paymentMethod) && (
        <Typography align="center" variant="body2" sx={{ mt: "12px", color: "#6B746B" }}>
          {[
            scheduleMessage,
            detailsMessage,
            !(Number(priceEstimate) > 0) ? "Price estimate must be greater than $0." : "",
            !paymentMethod ? "Add a payment method to publish this request." : "",
          ]
            .filter(Boolean)
            .join(" ")}
        </Typography>
      )}
      {!paymentMethod && (
        <Box
          sx={{
            mt: "32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <SvgIcon sx={{ width: "25px", height: "24px", mb: "8px" }}>
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="24"
                height="24"
                transform="translate(0.5)"
                fill="white"
              />
              <path
                d="M20.527 15.8019L14.7453 5.17367C13.8164 3.60982 11.5519 3.60774 10.6218 5.17367L4.84035 15.8019C3.89074 17.3999 5.04043 19.4235 6.90164 19.4235H18.4652C20.3249 19.4235 21.4766 17.4015 20.527 15.8019ZM12.6835 17.5055C12.1549 17.5055 11.7245 17.0751 11.7245 16.5465C11.7245 16.0178 12.1549 15.5874 12.6835 15.5874C13.2122 15.5874 13.6425 16.0178 13.6425 16.5465C13.6425 17.0751 13.2122 17.5055 12.6835 17.5055ZM13.6425 13.6694C13.6425 14.1981 13.2122 14.6284 12.6835 14.6284C12.1549 14.6284 11.7245 14.1981 11.7245 13.6694V8.87439C11.7245 8.34572 12.1549 7.91538 12.6835 7.91538C13.2122 7.91538 13.6425 8.34572 13.6425 8.87439V13.6694Z"
                fill="#F09E44"
              />
            </svg>
          </SvgIcon>
          <Typography
            align="center"
            variant="body1"
            sx={{
              mb: "20px",
            }}
          >
            You will be able to publish your request after adding a payment
            method.
          </Typography>
        </Box>
      )}
      <TermsOfServiceDialog open={open} onClose={handleClose} />
    </>
  );
};

const schema = yup.object().shape({
  name: yup.string().trim().required("Service name is required").max(120, "Service name must be 120 characters or less"),
  description: yup.string().trim().required("Description is required").max(2000, "Description must be 2,000 characters or less"),
  address: yup.string().required("Service address is required"),
  date: yup.date().required("Date is required"),
  weekdays: yup.array(),
  endMode: yup.string(),
  visitCount: yup.number().when(["repeats", "endMode"], {
    is: (repeats: unknown, endMode: string) =>
      (repeats === true || repeats === "true") && endMode === "visits",
    then: (s) => s.min(1, "Enter at least 1 visit").max(52, "52 visits maximum").required("Number of visits is required"),
    otherwise: (s) => s.notRequired(),
  }),
  startTime: yup.date().required("Start Time is required"),
  latitude: yup
    .number()
    .typeError("Select an address from the suggestions")
    .required("Select an address from the suggestions"),
  longitude: yup
    .number()
    .typeError("Select an address from the suggestions")
    .required("Select an address from the suggestions"),
  endTime: yup
    .date()
    .required("End Time is required")
    .test(
      "valid-window",
      "End time must be later the same day and at least 30 minutes after the start.",
      function (value) {
        const { startTime, date } = this.parent;
        return bookingWindowIssue(date, startTime, value) === null;
      }
    ),
  saveMethod: yup.boolean(),
  termsService: yup.boolean(),
  repeats: yup.boolean(),
  often: yup.string().when("repeats", {
    is: true,
    then: (s) => s.required("How often is required"),
    otherwise: (s) => s.notRequired(),
  }),
  expirationDate: yup.date().when(["repeats", "endMode"], {
    is: (repeats: unknown, endMode: string) =>
      (repeats === true || repeats === "true") && endMode !== "visits",
    then: (s) => s.required("End date is required"),
    otherwise: (s) => s.notRequired(),
  }),
});
